/**
 * useAudioPersistence — helpers para persistir preferencias del player de audio
 * ambiente en `localStorage`. Aislado para que el `EventAudioPlayer` no se
 * acople a la API del storage y sea facil de testear.
 *
 * Clave usada: `ceciluz_events_audio_muted` (PROMPT_MAESTRO §4.2 "Evento detalle").
 * Default: muted = true (respeta autoplay policy del navegador y la preferencia
 * de no asaltar al usuario con audio).
 */
import { useCallback, useEffect, useState } from 'react';

// Clave unica de localStorage. Prefijo `ceciluz_` para evitar colisiones con
// otras apps del mismo dominio durante el desarrollo.
export const AUDIO_MUTED_STORAGE_KEY = 'ceciluz_events_audio_muted';

/**
 * Lee el valor inicial desde localStorage. En SSR o si el browser bloquea el
 * storage (modo privado), devuelve el `initialMuted` recibido.
 */
function readInitialMuted(initialMuted: boolean): boolean {
  if (typeof window === 'undefined') return initialMuted;
  try {
    const raw = window.localStorage.getItem(AUDIO_MUTED_STORAGE_KEY);
    if (raw === null) return initialMuted;
    return raw === 'true';
  } catch {
    // SecurityError / QuotaExceededError en navegadores con storage deshabilitado.
    return initialMuted;
  }
}

/**
 * Hook que sincroniza el flag `muted` con localStorage.
 * Devuelve la tupla `[muted, setMuted]`. El setter persiste cada cambio.
 */
export function usePersistedMute(
  initialMuted = true,
): readonly [boolean, (next: boolean) => void] {
  // Lazy initializer para evitar tocar localStorage en cada render.
  const [muted, setMutedState] = useState<boolean>(() =>
    readInitialMuted(initialMuted),
  );

  // Persistir cada cambio. Si el storage falla, no rompe la UI: solo se pierde
  // la preferencia en proximas visitas.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(AUDIO_MUTED_STORAGE_KEY, muted ? 'true' : 'false');
    } catch {
      // Silencioso: el player sigue funcionando sin persistencia.
    }
  }, [muted]);

  const setMuted = useCallback((next: boolean): void => {
    setMutedState(next);
  }, []);

  return [muted, setMuted] as const;
}
